import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';

import {
  ActiveErrorsStore,
  ALLOWED_DIAGNOSIS_IDS,
  DevicesStore,
  InformationPopupStore,
  InstallationDetailsStore,
  RemoteServiceDiagnosisEntry,
  RemoteServiceDiagnosisLoadingStatus,
  RemoteServiceStore,
} from '@store';
import { take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { InformationPopupPage } from '../../../../shared/components/information-popup/information-popup.model';
import { RoutingService, UIService } from '../../../../shared/services';
import { RemoteServiceOverlayTypeEnum } from '../../installation-overlay.model';
import { InstallationService } from 'src/app/+installations/services/installation.service';

@Component({
  selector: 'app-remote-service-overlay',
  templateUrl: './remote-service-overlay.component.html',
  styleUrls: ['./remote-service-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoteServiceOverlayComponent implements OnInit, OnDestroy {
  InformationPopupPage = InformationPopupPage;
  RemoteServiceOverlayTypeEnum = RemoteServiceOverlayTypeEnum;
  prevGatewaySerial: string;
  anomalyList$: Observable<RemoteServiceDiagnosisEntry[]>;
  diagnosisList: RemoteServiceDiagnosisEntry[] = [];
  isMaintainerWithFreeOrBasicOrPlusPackage = false;
  readonly RemoteServiceDiagnosisLoadingStatus = RemoteServiceDiagnosisLoadingStatus;

  @Input() activeOverlay: RemoteServiceOverlayTypeEnum;
  @Input() installationId: number;
  @Input() gatewaySerial: string;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public activeErrorsStore: ActiveErrorsStore,
    public remoteServiceStore: RemoteServiceStore,
    public devicesStore: DevicesStore,
    private installationService: InstallationService,
    private uiService: UIService,
    private informationPopupStore: InformationPopupStore,
    private activatedRoute: ActivatedRoute,
    private routingService: RoutingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.informationPopupStore.loadPageSettings();
    this.anomalyList$ = this.remoteServiceStore.anomalyList$;

    combineLatest([
      this.installationDetailsStore.isMaintainerWithFreeOrBasicOrPlusPackage$,
      this.remoteServiceStore.diagnosisList$,
    ])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([isMaintainerWithFreeOrBasicOrPlusPackage, diagnosisList]) => {
        this.isMaintainerWithFreeOrBasicOrPlusPackage = isMaintainerWithFreeOrBasicOrPlusPackage;
        this.diagnosisList = diagnosisList || [];

        if (this.isMaintainerWithFreeOrBasicOrPlusPackage && this.diagnosisList.length) {
          this.diagnosisList = this.diagnosisList.filter((anomaly) =>
            ALLOWED_DIAGNOSIS_IDS.includes(anomaly.diagnosis_id)
          );
        }
      });
    this.remoteServiceStore.getLowBudgetNotification();
    this.prevGatewaySerial = this.gatewaySerial;
    this.activatedRoute.queryParams
      .pipe(
        take(1),
        withLatestFrom(this.installationDetailsStore.getDeviceSerialForRemoteDiagnostics$)
      )
      .subscribe(([params, deviceSerial]) => {
        if (params?.deviceSerial !== deviceSerial) {
          this.routingService.updateUrl({ deviceSerial });
        }
        this.cdr.markForCheck();
      });
  }

  closeOverlay(): void {
    this.installationService.switchSelectionToMainDevice();
    this.uiService.closeActiveOverlay(this.prevGatewaySerial, this.installationId, true);
  }

  hideLowBudgetNotification(): void {
    this.remoteServiceStore.hideLowBudgetNotification();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
